import * as en from '@/locales/en-US';
import * as zh from '@/locales/zh-CN';
import { encode } from '@/utils/cryptoUnreverse';
import { message } from 'antd';
import fetch from 'dva/fetch';
import { getLocale } from 'umi/locale';
import { cacheLoginKey, getDiffTime, getLoginId, getTokens, loginKey, setTokens } from './LoginContext';
import { BATCHVIEW_REQUEST_NORMAL_TIME_OUT, LOGIN_JWT_KEY, REQUEST_NORMAL_TIME_OUT, REQUEST_WAIT_TIME_OUT, REQUEST_WAIT_TIME_OUT_HOUR } from './constants';
import { isAntdPro } from './utils';

function formatMessage({ id }) {
  if (getLocale() === 'zh-CN') {
    return zh.default[id] ? zh.default[id] : id
  } else if (getLocale() === 'en-US') {
    return en.default[id] ? en.default[id] : id
  } else {
    return null
  }
}
const codeMessage = {
  200: formatMessage({ id: '服务器成功返回请求的数据。' }),
  201: formatMessage({ id: '新建或修改数据成功。' }),
  202: formatMessage({ id: '一个请求已经进入后台排队（异步任务）。' }),
  204: formatMessage({ id: '删除数据成功。' }),
  400: formatMessage({ id: '发出的请求有错误，服务器没有进行新建或修改数据的操作。' }),
  401: formatMessage({ id: '用户没有权限（令牌、用户名、密码错误）。' }),
  403: formatMessage({ id: '用户得到授权，但是访问是被禁止的。' }),
  404: formatMessage({ id: '发出的请求针对的是不存在的记录，服务器没有进行操作。' }),
  406: formatMessage({ id: '请求的格式不可得。' }),
  410: formatMessage({ id: '请求的资源被永久删除，且不会再得到的。' }),
  422: formatMessage({ id: '当创建一个对象时，发生一个验证错误。' }),
  500: formatMessage({ id: '服务器发生错误，请检查服务器。' }),
  502: formatMessage({ id: '网关错误。' }),
  503: formatMessage({ id: '服务不可用，服务器暂时过载或维护。' }),
  504: formatMessage({ id: '网关超时。' }),
};

const localMap = {
  'zh-CN': 'zh_CN',
  'zh-TW': 'zh_TW',
  'en-US': 'en_US',
};

const checkStatus = response => {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  const error = new Error();
  error.response = response;
  error.name = response.status;
  throw error;
};

function cacheLogin(response) {

  const authorization = response.headers.get(LOGIN_JWT_KEY);
  if (!authorization || authorization === loginKey()) {
    return response;
  }

  cacheLoginKey(authorization);

  return response;
}

/**
 * 获取超时时间
 *
 * @param {*} url 请求url
 */
function getTimeoutTime(url) {
  if (!url) {
    return 0;
  }

  let batchImportMatched = url.match(/batchImport/gi);
  if (batchImportMatched) {
    return REQUEST_WAIT_TIME_OUT;
  }

  let batchreview = url.match(/batchreview/gi);
  if (batchreview) {
    return BATCHVIEW_REQUEST_NORMAL_TIME_OUT;
  }
  let print = url.match(/\/pick\/realPrintLabel/gi);
  if (print) {
    return REQUEST_WAIT_TIME_OUT_HOUR
  }
  return REQUEST_NORMAL_TIME_OUT;
}


function getToken() {
  // 从sessionStorage中获取tokens信息
  let tokenObj = getTokens() ? JSON.parse(getTokens()) : null;
  if (tokenObj == null || tokenObj == undefined) {
    return null;
  }

  if (tokenObj.accessToken != undefined) {
    let tokenArr = tokenObj.accessToken.split('.');
    if (tokenArr.length === 3) {
      const tokenStr = tokenArr[1].replace(/-/g, '+').replace(/_/g, '/');
      const tokenInfo = JSON.parse(window.atob(tokenStr));
      const currentTime = Math.floor(new Date().getTime());
      // 当前时间戳,向下取整
      // 判断token失效时间，若失效则将token替换成refreshToken
      if (tokenInfo.exp * 1000 < currentTime + getDiffTime() ? getDiffTime() : 0) {
        // token失效
        return tokenObj.refreshToken;
      } else {
        // 未过失效时间
        return tokenObj.accessToken;
      }
    } else {
      return tokenObj.accessToken;
    }
  }

  return null;
}


/**
 * Requests a URL, returning a promise.
 * @param {string} url The URL we want to request
 * @param {object} option he options we want to pass to "fetch"
 * @param {object} ingoreTimeOut 是否忽略超时比较 传值则忽略
 * @return {object}       An object containing either "data" or "err"
 */
export default function request(url, option, ingoreTimeOut) {
  url = window.g_configs.API_SERVER + url;

  if (url.indexOf('?') > -1) url = url + '&lang=' + localMap[getLocale()];
  else url = url + '?lang=' + localMap[getLocale()];

  const options = {
    expirys: isAntdPro(),
    ...option,
  };

  const defaultOptions = {
    credentials: 'include',
  };
  let newOptions = { ...defaultOptions, ...options };

  if (
    newOptions.method === 'POST' ||
    newOptions.method === 'PUT' ||
    newOptions.method === 'DELETE'
  ) {
    if (!(newOptions.body instanceof FormData)) {
      newOptions.headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=utf-8',
        ...newOptions.headers,
        iwmsJwt: loginKey(),
      };
      newOptions.body = JSON.stringify(newOptions.body);
    } else {
      // newOptions.body is FormData
      newOptions.headers = {
        Accept: 'application/json',
        'Content-Type': 'multipart/form-DataTransfer',
        ...newOptions.headers,
        iwmsJwt: loginKey(),
      };
    }
  } else {
    newOptions.headers = {
      iwmsJwt: loginKey(),
      ...newOptions.headers,
    };
  }

  let loginId = getLoginId();
  let source = url.replace(window.g_configs.API_SERVER, "") + "&&" + loginId;
  if (newOptions.body) {
    source = source + "@@" + newOptions.body;
  }
  let sercet = encode(source);
  newOptions.headers = {
    iwmsSecret: sercet,
    ...newOptions.headers,
  };

  const controller = new AbortController()
  const signal = controller.signal

  if (!navigator.onLine) {
    message.error(formatMessage({ id: '当前网络不可用' }));
    controller.abort();
    return {
      'error': 'no network'
    };
  }

  return Promise.race([
    fetch(url, newOptions, { signal })
      .then(checkStatus)
      .then(cacheLogin),
    new Promise(function (resolve, reject) {  //10秒后执行，如果后端接口没有返回，则直接返回timeout
      if (!ingoreTimeOut && url.indexOf('iwms-report') < 0) {
        setTimeout(() => reject('timeout'), getTimeoutTime(url))
      }
    })
  ]).then(response => {
    if (newOptions.responseType && newOptions.responseType === 'blob') {
      return response.blob();
    }
    const res = response.json();
    res.then(body => {
      if (body && !body.success && url.indexOf('/check') < 0 && (!localStorage.getItem("showMessage") || localStorage.getItem("showMessage") === '1')) {
        message.error(body.message);
      }
    });

    if (response.url.indexOf('login/uniLogin?action=change-token') > -1) {
      let accessToken = response.headers.get('access-token');
      if (accessToken) {
        setTokens(accessToken)
      }
    }
    return res;
  })
    .catch(e => {
      if (!navigator.onLine) {
        message.error(formatMessage({ id: '当前网络不可用' }))
        controller.abort();
        return {
          'error': 'no network'
        };
      }
      if (e == 'timeout') {
        message.error(formatMessage({ id: '请求超时' }))
        controller.abort();
        return {
          'error': 'timeout'
        };
      } else {
        const status = e.name;
        if (status === 401) {
          window.g_app._store.dispatch({
            type: 'login/logout',
          });
          message.error(formatMessage({ id: "登录信息失效，请重新登录！" }));
          return e.response.json();
        }
        const res = e.response.json();
        res.then(result => {
          if (!localStorage.getItem("showMessage") || localStorage.getItem("showMessage") === '1') {
            message.error(result.message);
          }
        });
        return res;
      }
    });
}
